import { useDispatch, useSelector } from 'react-redux';
import { getAuthenticationUserId, getAuthenticationUserName } from 'data/authentication/authentication.selectors';
import { createTargetDomain } from 'data/targetDomain/targetDomain.actions';
import { getTargetDomainCreatedId, getTargetDomainList } from 'data/targetDomain/targetDomain.selectors';
import { TargetDomain } from 'data/targetDomain/targetDomain.types';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import { useEffect, useRef } from 'react';
import { FieldUpdateHandlerParam } from 'shared/form';
import { isDiscoverySettingAuditEnabled } from 'xcipio/discovery-xcipio-selectors';
import { TargetDomainFormFieldHandler } from '../components/TargetDomainForm';
import { getAuditDetailsToSubmit } from '../utils/targetDomainTemplateDataToSubmit';

// eslint-disable-next-line import/prefer-default-export
export function useFieldCreateNewTargetDomain(
  handleFieldUpdate: TargetDomainFormFieldHandler,
): [TargetDomainFormFieldHandler] {
  const deliveryFunctionId = useSelector(getSelectedDeliveryFunctionId);
  const isAuditEnabled = useSelector(isDiscoverySettingAuditEnabled);
  const userId = useSelector(getAuthenticationUserId);
  const userName = useSelector(getAuthenticationUserName);
  const createdId = useSelector(getTargetDomainCreatedId);
  const targetDomainList = useSelector(getTargetDomainList);
  const dispatch = useDispatch();
  const paramRef = useRef<FieldUpdateHandlerParam>();

  useEffect(() => {
    if (createdId == null || paramRef.current == null) {
      return;
    }

    const createdTargetDomain = targetDomainList.find(({ domain }) => domain === createdId);
    if (createdTargetDomain == null) {
      // The list is not up to date yet, so wait.
      return;
    }

    const param = paramRef.current;
    if (param == null) {
      return;
    }

    paramRef.current = undefined;

    param.value = createdId;

    handleFieldUpdate(param);

  }, [createdId, targetDomainList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldCreateNew: TargetDomainFormFieldHandler = (param) => {
    const targetDomain: TargetDomain = {
      id: '',
      domain: param.valueLabel,
      description: '',
    };

    const payload = {
      data: targetDomain,
      auditDetails: getAuditDetailsToSubmit(
        targetDomain,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    dispatch(createTargetDomain(deliveryFunctionId, payload));

    paramRef.current = param;
  };

  return [handleFieldCreateNew];
}
